'use client'

import { useCallback, useEffect, useRef } from 'react'
import { groupBy } from 'lodash'
import { Feed, FeedEventPayload, FeedItem } from '@knocklabs/client'
import { NotificationEventName } from '~/domain/enums/enum.schema'
import {
  NOTIFICATION_LIMIT,
  NOTIFICATION_REMOVE_DELAY,
} from '~/scalis-components/core/notification/notification.constants'
import { NotificationKind } from '~/scalis-components/core/notification/notification.types'
import { useNotification } from '~/scalis-components/core/notification/useNotification'
import { useKnockProviderContext } from '~/src/providers/knock-provider-wrapper/knock-provider-context'
import { getNotificationLink } from '../../navbar-template.utils'

type NotificationToasterContentProps = {
  feedClient: Feed
  feedItems: FeedItem[]
}

export const NotificationToasterContent = ({ feedClient, feedItems }: NotificationToasterContentProps) => {
  const { notification } = useNotification()

  // This is used to prevent the initial notifications from being shown again, since marking all as seen triggers re-render,
  // because the feedItems are changed, which triggers the notifications to be shown again.
  const hasShownInitialNotifications = useRef(false)

  const unseenNewApplicantNotifications = feedItems.filter(
    (item) => item.data?.notificationName === NotificationEventName.NEW_APPLICANT_TO_MY_JOB && !item.seen_at,
  )

  const showNotificationGroup = useCallback(
    (notifications: FeedItem[]) => {
      notifications.forEach((notificationItem: FeedItem) => {
        notification({
          title: notificationItem.data?.title || notificationItem.activities[0].data?.title,
          description: notificationItem.data?.description || notificationItem.activities[0].data?.description,
          kind: NotificationKind.info,
          action: getNotificationLink(notificationItem)
            ? {
                label: 'View',
                href: getNotificationLink(notificationItem),
                icon: 'fa-regular fa-arrow-right',
              }
            : undefined,
        })
      })
    },
    [notification],
  )

  const onNotificationsReceived = useCallback(
    (payload: FeedEventPayload) => {
      const notSeenNotifications = payload.items.filter((notificationItem: FeedItem) => !notificationItem.seen_at)

      // Split notifications into groups
      const groupSize = NOTIFICATION_LIMIT
      const groups = []
      for (let i = 0; i < notSeenNotifications.length; i += groupSize) {
        groups.push(notSeenNotifications.slice(i, i + groupSize))
      }

      // Show each group with a delay
      groups.forEach((group, index) => {
        setTimeout(() => {
          showNotificationGroup(group)
        }, index * NOTIFICATION_REMOVE_DELAY) // Delay between groups
      })

      // Mark notifications as seen
      feedClient.markAllAsSeen()
    },
    [feedClient, showNotificationGroup],
  )

  const showUnseenNewApplicantNotifications = useCallback(async () => {
    const groups = groupBy(unseenNewApplicantNotifications, (notification) => notification.data?.requisitionId)

    for (const group in groups) {
      notification({
        title: `New Applicants for ${groups[group][0].data?.jobTitle}`,
        kind: NotificationKind.info,
        action: getNotificationLink(groups[group][0])
          ? {
              label: 'View',
              href: getNotificationLink(groups[group][0]),
              icon: 'fa-regular fa-arrow-right',
            }
          : undefined,
      })

      await feedClient.markAsSeen(groups[group])
    }
  }, [feedClient, notification, unseenNewApplicantNotifications])

  useEffect(() => {
    // Receive all real-time notifications on our feed
    feedClient.on('items.received.realtime', onNotificationsReceived)
    // Only show initial notifications once
    if (!hasShownInitialNotifications.current && unseenNewApplicantNotifications.length > 0) {
      showUnseenNewApplicantNotifications()
      hasShownInitialNotifications.current = true
    }

    return () => feedClient.off('items.received.realtime', onNotificationsReceived)
  }, [feedClient, onNotificationsReceived, showUnseenNewApplicantNotifications, unseenNewApplicantNotifications.length])

  return null // The notifications are rendered by the Notification component
}

const NavbarNotificationToaster = ({ feedClient, feedItems }: NotificationToasterContentProps) => {
  const { isKnockInitialized } = useKnockProviderContext()

  if (!isKnockInitialized) {
    return null
  }

  return (
    <NotificationToasterContent
      feedClient={feedClient}
      feedItems={feedItems}
    />
  )
}

export default NavbarNotificationToaster
